import React, { useState, useEffect } from "react";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Box, Typography, IconButton } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { cameraService } from "client-app-core";
import PropTypes from "prop-types";
import { getTranslation } from "orion-components/i18n";

const CameraSettingsDialog = ({ open, onClose, camera }) => {
	const [focusNearPressed, setFocusNearPressed] = useState(false);
	const [focusFarPressed, setFocusFarPressed] = useState(false);
	const [irisClosePressed, setIrisClosePressed] = useState(false);
	const [irisOpenPressed, setIrisOpenPressed] = useState(false);
	const [isFocusMoving, setIsFocusMoving] = useState(false);
	const [isIrisMoving, setIsIrisMoving] = useState(false);
	const supportesFocus = camera?.entityData?.properties?.features?.includes("focus") ?? false;
	const supportesIris = camera?.entityData?.properties?.features?.includes("iris") ?? false;

	const sendFocusContinuous = direction => {
		if (!camera || !camera.id) {
			return;
		}

		if (direction !== 0) {
			setIsFocusMoving(true);
		}

		cameraService.focusContinuous(camera.id, direction, err => {
			if (err) {
				console.error("Error setting focus continuous:", err);
			}
		});
	};

	const sendIrisContinuous = direction => {
		if (!camera || !camera.id) {
			return;
		}

		if (direction !== 0) {
			setIsIrisMoving(true);
		}

		cameraService.irisContinuous(camera.id, direction, err => {
			if (err) {
				console.error("Error setting iris continuous:", err);
			}
		});
	};

	const stopFocusContinuous = () => {
		if (!camera || !camera.id || !isFocusMoving) {
			return;
		}

		setIsFocusMoving(false);
		cameraService.focusContinuous(camera.id, 0, err => {
			if (err) {
				console.error("Error stopping focus continuous:", err);
			}
		});
	};

	const stopIrisContinuous = () => {
		if (!camera || !camera.id || !isIrisMoving) {
			return;
		}

		setIsIrisMoving(false);
		cameraService.irisContinuous(camera.id, 0, err => {
			if (err) {
				console.error("Error stopping iris continuous:", err);
			}
		});
	};

	const stopAllContinuous = () => {
		stopFocusContinuous();
		stopIrisContinuous();
	};

	const handleDialogClose = () => {
		stopAllContinuous();
		onClose();
	};

	const handleSave = () => {
		handleDialogClose();
	};

	const handleFocusNearStop = () => {
		setFocusNearPressed(false);
		stopFocusContinuous();
	};

	const handleFocusFarStop = () => {
		setFocusFarPressed(false);
		stopFocusContinuous();
	};

	const handleIrisCloseStop = () => {
		setIrisClosePressed(false);
		stopIrisContinuous();
	};

	const handleIrisOpenStop = () => {
		setIrisOpenPressed(false);
		stopIrisContinuous();
	};

	return (
		<Dialog
			open={open}
			onClose={handleDialogClose}
			maxWidth="xs"
			PaperProps={{
				style: {
					backgroundColor: "#212327",
					padding: "12px",
					minWidth: "360px"
				}
			}} data-testid="orion-components-cbcomponents-new-video-player-components-main-menu-components-camera-settings-dialog-dialog"
		>
			<DialogTitle sx={{ color: "#fff" }} data-testid="orion-components-cbcomponents-new-video-player-components-main-menu-components-camera-settings-dialog-dialog-title">
				{getTranslation("global.CBComponents.newVideoPlayer.components.mainMenu.CameraSettingsDialog.title")}
			</DialogTitle>
			<DialogContent data-testid="orion-components-cbcomponents-new-video-player-components-main-menu-components-camera-settings-dialog-dialog-content">
				<Typography sx={{ color: "#fff", mb: 2 }}>
					{getTranslation(
                        "global.CBComponents.newVideoPlayer.components.mainMenu.CameraSettingsDialog.mainDescription"
                    )}
				</Typography>
				{supportesFocus && (
					<Box sx={{ mb: 2.5, mt: 1 }}>
						<Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-start", gap: 1.5 }}>
							<Typography sx={{ color: "#fff", fontSize: 22, lineHeight: 1 }}>
								{getTranslation(
									"global.CBComponents.newVideoPlayer.components.mainMenu.CameraSettingsDialog.focus"
								)}
							</Typography>
							<Box sx={{ display: "flex", alignItems: "center", ml: "auto" }}>
								<IconButton
									title="Near"
									size="small"
									sx={{
										color: "#4DB5F4",
										p: 0.5,
										mr: 1,
										cursor: "pointer",
										userSelect: "none",
										borderRadius: 1,
										backgroundColor: focusNearPressed ? "rgba(77, 181, 244, 0.35)" : "transparent",
										transition: "background-color 0.1s",
										"&:hover": {
											backgroundColor: focusNearPressed
												? "rgba(77, 181, 244, 0.35)"
												: "rgba(77, 181, 244, 0.1)"
										}
									}}
									onMouseDown={() => {
										setFocusNearPressed(true);
										sendFocusContinuous(-1);
									}}
									onMouseUp={handleFocusNearStop}
									onMouseLeave={handleFocusNearStop}
									onTouchStart={() => {
										setFocusNearPressed(true);
										sendFocusContinuous(-1);
									}}
									onTouchEnd={handleFocusNearStop} data-testid="orion-components-cbcomponents-new-video-player-components-main-menu-components-camera-settings-dialog-icon-button"
								>
									<RemoveIcon fontSize="small" />
								</IconButton>
								<IconButton
									title="Far"
									size="small"
									sx={{
										color: "#4DB5F4",
										p: 0.5,
										cursor: "pointer",
										userSelect: "none",
										borderRadius: 1,
										backgroundColor: focusFarPressed ? "rgba(77, 181, 244, 0.35)" : "transparent",
										transition: "background-color 0.1s",
										"&:hover": {
											backgroundColor: focusFarPressed
												? "rgba(77, 181, 244, 0.35)"
												: "rgba(77, 181, 244, 0.1)"
										}
									}}
									onMouseDown={() => {
										setFocusFarPressed(true);
										sendFocusContinuous(1);
									}}
									onMouseUp={handleFocusFarStop}
									onMouseLeave={handleFocusFarStop}
									onTouchStart={() => {
										setFocusFarPressed(true);
										sendFocusContinuous(1);
									}}
									onTouchEnd={handleFocusFarStop} data-testid="orion-components-cbcomponents-new-video-player-components-main-menu-components-camera-settings-dialog-icon-button-2"
								>
									<AddIcon fontSize="small" />
								</IconButton>
							</Box>
						</Box>
						<Typography sx={{ color: "#B2B6BB", mt: 0.5, fontSize: 12 }}>
							{getTranslation(
                                "global.CBComponents.newVideoPlayer.components.mainMenu.CameraSettingsDialog.focusDescription"
                            )}
						</Typography>
					</Box>
				)}
				{supportesIris && (
					<Box sx={{ mb: 2 }}>
						<Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-start", gap: 1.5 }}>
							<Typography sx={{ color: "#fff", fontSize: 22, lineHeight: 1 }}>
								{getTranslation(
									"global.CBComponents.newVideoPlayer.components.mainMenu.CameraSettingsDialog.iris"
								)}
							</Typography>
							<Box sx={{ display: "flex", alignItems: "center", ml: "auto" }}>
								<IconButton
									title="Close"
									size="small"
									sx={{
										color: "#4DB5F4",
										p: 0.5,
										mr: 1,
										cursor: "pointer",
										userSelect: "none",
										borderRadius: 1,
										backgroundColor: irisClosePressed ? "rgba(77, 181, 244, 0.35)" : "transparent",
										transition: "background-color 0.1s",
										"&:hover": {
											backgroundColor: irisClosePressed
												? "rgba(77, 181, 244, 0.35)"
												: "rgba(77, 181, 244, 0.1)"
										}
									}}
									onMouseDown={() => {
										setIrisClosePressed(true);
										sendIrisContinuous(-1);
									}}
									onMouseUp={handleIrisCloseStop}
									onMouseLeave={handleIrisCloseStop}
									onTouchStart={() => {
										setIrisClosePressed(true);
										sendIrisContinuous(-1);
									}}
									onTouchEnd={handleIrisCloseStop} data-testid="orion-components-cbcomponents-new-video-player-components-main-menu-components-camera-settings-dialog-icon-button-3"
								>
									<RemoveIcon fontSize="small" />
								</IconButton>
								<IconButton
									title="Open"
									size="small"
									sx={{
										color: "#4DB5F4",
										p: 0.5,
										cursor: "pointer",
										userSelect: "none",
										borderRadius: 1,
										backgroundColor: irisOpenPressed ? "rgba(77, 181, 244, 0.35)" : "transparent",
										transition: "background-color 0.1s",
										"&:hover": {
											backgroundColor: irisOpenPressed
												? "rgba(77, 181, 244, 0.35)"
												: "rgba(77, 181, 244, 0.1)"
										}
									}}
									onMouseDown={() => {
										setIrisOpenPressed(true);
										sendIrisContinuous(1);
									}}
									onMouseUp={handleIrisOpenStop}
									onMouseLeave={handleIrisOpenStop}
									onTouchStart={() => {
										setIrisOpenPressed(true);
										sendIrisContinuous(1);
									}}
									onTouchEnd={handleIrisOpenStop} data-testid="orion-components-cbcomponents-new-video-player-components-main-menu-components-camera-settings-dialog-icon-button-4"
								>
									<AddIcon fontSize="small" />
								</IconButton>
							</Box>
						</Box>
						<Typography sx={{ color: "#B2B6BB", mt: 0.5, fontSize: 12 }}>
							{getTranslation(
                                "global.CBComponents.newVideoPlayer.components.mainMenu.CameraSettingsDialog.irisDescription"
                            )}
						</Typography>
					</Box>
				)}
			</DialogContent>
			<DialogActions data-testid="orion-components-cbcomponents-new-video-player-components-main-menu-components-camera-settings-dialog-dialog-actions">
				<Button onClick={handleDialogClose} variant="text" style={{ color: "#B2B6BB" }} data-testid="orion-components-cbcomponents-new-video-player-components-main-menu-components-camera-settings-dialog-button">
					{getTranslation(
						"global.CBComponents.newVideoPlayer.components.mainMenu.CameraSettingsDialog.cancel"
					)}
				</Button>
				<Button
					onClick={handleSave}
					variant="contained"
					sx={{
						color: "#fff"
					}} data-testid="orion-components-cbcomponents-new-video-player-components-main-menu-components-camera-settings-dialog-button-2"
				>
					{getTranslation("global.CBComponents.newVideoPlayer.components.mainMenu.CameraSettingsDialog.save")}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

CameraSettingsDialog.propTypes = {
	open: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	camera: PropTypes.object
};

export default CameraSettingsDialog;
